// F16 — Our Services, rewritten as what the system does rather than a list of
// services sold around it.
//
// What was deleted here, and why:
//
// - The three pricing tiers (Basic / Pro / Premium). They gated real features
//   behind invented plan boundaries — inventory and expenses were "Pro",
//   reporting was "Premium" — and none of that gating exists in the product.
//   Pricing is one price per venue. No figure is published, so none is shown.
// - "Most Popular" on the Premium tier. Nothing has been sold; there is no
//   popularity to report.
// - "Quarterly Strategy Reviews", "Dedicated Support", "Priority Support" and
//   support SLAs "based on your chosen plan". No SLA has been written and no
//   support tier exists.
// - "Get operational in days, not weeks", "Our proven process", "A proven
//   step-by-step approach". Nothing has been run enough times to be proven.
// - "Hardware Guidance" advertising advice on printers, drawers and network
//   layout, and "Data Migration" from previous POS systems. Neither is a
//   service the business currently delivers.
// - The desktop references throughout. Electron is legacy; the product is two
//   native apps plus the browser portal.
//
// Section order: hero, the floor, the back office, one record, where it runs,
// what it costs, contact.
import React from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, FONT, TYPE, SP, R, W, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';
import StoreBadges from '../../components/landing/StoreBadges';

// auto-fit grids stack on their own below the min track, so the two-column
// and three-column sections need no media query and mirror cleanly in RTL.
const grid = (min) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    gap: SP.xl,
});

const OurServicesPage = () => {
    const { t } = useLanguage();

    const floor = [
        {
            src: '/screenshots/cashier-till.png',
            alt: t('The Aqana till showing the product grid, a live cart and the payment buttons'),
            title: t('Till and payments'),
            body: t('Ring up the full menu with add-ons, splits and discounts, then take cash, card or a split payment. The receipt prints and the sale is recorded in the same moment.'),
        },
        {
            src: '/screenshots/waiter-phone.png',
            alt: t('A waiter sending a table order from a phone in the Aqana app'),
            title: t('Table service'),
            body: t('Take the order at the table on a phone, course by course, against the table it belongs to. It reaches the kitchen before the waiter gets back.'),
        },
        {
            src: '/screenshots/kitchen-display.png',
            alt: t('The Aqana kitchen display showing open tickets with their items and notes'),
            title: t('Kitchen display'),
            body: t('Tickets arrive on screen in the order they were sent, with add-ons and notes attached. Chefs clear them as the food leaves the pass.'),
        },
    ];

    const backOffice = [
        {
            title: t('Rota and payroll'),
            body: t('Build the week by station, then log advances, allowances and salary against the same staff record. Run the pay cycle from the shifts that were actually worked.'),
        },
        {
            title: t('Stock and recipes'),
            body: t('Hold ingredients with their costs and attach a recipe to each dish. Selling the dish takes its ingredients out of stock.'),
        },
        {
            title: t('Expenses'),
            body: t('Record daily spend and standing overheads against the day they belong to. Rent, wages and suppliers sit beside the takings they came out of.'),
        },
        {
            title: t('Insights'),
            body: t('See margin by dish, takings by hour and cost by category, day by day. The figures are built from the sales already in the system.'),
        },
    ];

    const insights = [
        t('Compare a day against the same day last month.'),
        t('See which dishes earn and which only sell.'),
        t('Watch takings by hour before you write the rota.'),
        t('Track expenses against revenue without a spreadsheet.'),
    ];

    const MarkedItem = ({ children }) => (
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: SP.md }}>
            <span style={{ marginBlockStart: 3 }}>
                <ArchMark size={15} color={C.inkFaint} />
            </span>
            <span style={{ ...TYPE.body, color: C.inkMuted }}>{children}</span>
        </li>
    );

    const framed = {
        width: '100%',
        height: 'auto',
        display: 'block',
        borderRadius: R.md,
        border: `1px solid ${C.line}`,
        background: C.surface,
    };

    const indexNumeral = {
        fontFamily: FONT.display,
        fontSize: '0.8125rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        color: C.inkFaint,
        display: 'block',
        marginBlockEnd: SP.sm,
    };

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: 760 }}>
                        <span style={S.label}>{t('WHAT AQANA DOES')}</span>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Seven parts of the day, one system.')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, maxWidth: W.prose, marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                            {t('Take the order, feed the kitchen, pay the staff, count the stock, see the margin. Each part writes to the same record, so nothing is typed twice.')}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: SP.md }}>
                            <Link to="/contact-us" style={S.btnPrimary}>{t('Get a demo')}</Link>
                            <Link to="/how-it-works" style={S.btnSecondary}>{t('See how it works')}</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- 2. The floor ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={S.container}>
                    <span style={S.label}>{t('THE FLOOR')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Where the order is taken.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('The same order, wherever it starts. Nothing is re-keyed between the table, the till and the pass.')}
                    </p>

                    <div style={grid(300)}>
                        {floor.map((item, index) => (
                            <article key={item.title} style={{ ...S.card, padding: SP.lg }}>
                                <img src={item.src} alt={item.alt} loading="lazy" style={framed} />
                                <span style={{ ...indexNumeral, marginBlockStart: SP.lg }}>{`0${index + 1}`}</span>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{item.title}</h3>
                                <p style={{ ...TYPE.body, color: C.inkMuted, marginBlockStart: SP.sm }}>{item.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 3. The back office ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <span style={S.label}>{t('THE BACK OFFICE')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Where the week is planned.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('The half of the job that happens after close. It runs on the same data the till writes.')}
                    </p>

                    <div style={{ ...grid(260), gap: SP['2xl'] }}>
                        {backOffice.map((item, index) => (
                            <div key={item.title} style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                <span style={indexNumeral}>{`0${index + 4}`}</span>
                                <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{item.title}</h3>
                                <p style={{ ...TYPE.body, color: C.inkMuted, marginBlockStart: SP.sm }}>{item.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- 4. One record ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, ...grid(400), gap: SP['4xl'], alignItems: 'center' }}>
                    <div style={{ ...S.card, padding: SP.md }}>
                        <img
                            src="/screenshots/manager-analytics.png"
                            alt={t('The Aqana analytics view showing product performance and margin')}
                            loading="lazy"
                            style={framed}
                        />
                    </div>

                    <div>
                        <span style={S.label}>{t('ONE RECORD')}</span>
                        <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Nothing to export.')}
                        </h2>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, marginBlock: `${SP.lg}px ${SP.xl}px` }}>
                            {t('The till writes to the same place the reports read from. There is no nightly upload and no spreadsheet in the middle.')}
                        </p>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                            {insights.map((item) => <MarkedItem key={item}>{item}</MarkedItem>)}
                        </ul>
                    </div>
                </div>
            </section>

            {/* ---------- 5. Where it runs ---------- */}
            <section style={S.section({ tight: true })}>
                <div style={S.container}>
                    <span style={S.label}>{t('WHERE IT RUNS')}</span>
                    <h2 style={{ ...TYPE.displayL, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('Two apps and a browser.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px ${SP['3xl']}px` }}>
                        {t('Aqana POS runs the floor on iPad and iPhone. Aqana Manager puts the back office in your pocket, and the full portal opens on a laptop.')}
                    </p>

                    <div style={grid(280)}>
                        <div style={S.card}>
                            <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{t('Aqana POS')}</h3>
                            <p style={{ ...TYPE.body, color: C.inkMuted, marginBlock: `${SP.sm}px ${SP.xl}px` }}>
                                {t('Till, table service and the kitchen display.')}
                            </p>
                            <StoreBadges app="pos" align="start" />
                        </div>

                        <div style={S.card}>
                            <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{t('Aqana Manager')}</h3>
                            <p style={{ ...TYPE.body, color: C.inkMuted, marginBlock: `${SP.sm}px ${SP.xl}px` }}>
                                {t('Rotas, payroll, stock, expenses and the day figures.')}
                            </p>
                            <StoreBadges app="manager" align="start" />
                        </div>

                        <div style={S.card}>
                            <h3 style={{ ...TYPE.title, color: C.ink, margin: 0 }}>{t('The browser portal')}</h3>
                            <p style={{ ...TYPE.body, color: C.inkMuted, marginBlock: `${SP.sm}px ${SP.xl}px` }}>
                                {t('The whole back office on a laptop, in your team\'s language.')}
                            </p>
                            <a
                                href="/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={S.btnSecondary}
                            >
                                {t('Open the portal')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------- 6. What it costs ---------- */}
            <section style={S.section({ deep: true, tight: true })}>
                <div style={S.container}>
                    <span style={S.label}>{t('PRICING')}</span>
                    <h2 style={{ ...TYPE.displayM, color: C.ink, margin: 0, textWrap: 'balance' }}>
                        {t('One price, per venue.')}
                    </h2>
                    <p style={{ ...S.prose, marginBlock: `${SP.lg}px 0` }}>
                        {t('A monthly subscription for the software, covering every till and every member of staff. Hardware is yours. We quote per venue, so tell us how many sites and screens you run.')}
                    </p>
                    <p style={{ ...TYPE.caption, color: C.inkFaint, marginBlockStart: SP.md }}>
                        {t('UK venues are quoted in £.')}
                    </p>
                </div>
            </section>

            {/* ---------- 7. Contact ---------- */}
            <section
                style={{
                    ...S.section(),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={420}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -60, insetBlockStart: -40, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('Set it up on your own menu.')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('Send us your menu and prices. We will load them and walk you through a service on the real thing.')}
                    </p>
                    <Link
                        to="/contact-us"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Contact us')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default OurServicesPage;
